import { LOCATION_CHANGE } from 'connected-react-router'
import { merge } from 'lodash'

// Types
import { EntitiesState, RoutingState, Country, Subscription } from 'types'

// Utils
import { COUNTRIES } from '@constants'

export const initialRoutingState: RoutingState = {
  locationBeforeTransitions: '',
}

export const routing = (state: RoutingState = initialRoutingState, action: { type: string; payload: string }): RoutingState => {
  if (action.type === LOCATION_CHANGE) {
    return {
      ...state,
      locationBeforeTransitions: action.payload,
    }
  }
  return state
}

// generic reducer to update entities if exists in response
export const initialEntities: EntitiesState = {
  artists: {},
  beats: {},
  changePaymentMethodOrders: {},
  collections: {},
  comments: {},
  countries: COUNTRIES,
  countryOptions: Object.keys(COUNTRIES)
    .map((id: string) => COUNTRIES[id])
    .sort((a: Country, b: Country) => a.country_name.localeCompare(b.country_name)),
  creditPackOffers: {},
  creditPackOrders: {},
  currencies: {},
  discounts: {},
  distributors: {},
  genres: {},
  invites: {},
  labels: {},
  licenseRevenues: {},
  licenseTypes: {},
  licenseVersions: {},
  notifications: {},
  pages: {},
  pageSummaries: {},
  payments: {},
  profiles: {},
  pros: {},
  recommendationsArtist: {},
  recommendationsCollection: {},
  recommendationsGenre: {},
  recommendationsSong: {},
  regions: {},
  registrationServices: {},
  revenueOrders: {},
  revenueReportPeriods: {},
  sampleLicenseOrders: {},
  savedPaymentMethods: {},
  searchconfig: {},
  songs: {},
  subscriptionOrders: {},
  subscriptions: {},
  tierOffers: {},
  tracks: {},
  trackTypes: {},
  users: {},
  userSongRevenueReports: {},
  userSongs: {},
}

type APIDoneAction = {
  type: string
  data: unknown
  requestKey?: string
}

type DeleteUserSongDoneAction = APIDoneAction & {
  type: 'DELETE_USER_SONG_DONE'
  data: { userSongId: number }
}

type CollectionDeleteDoneAction = APIDoneAction & {
  type: 'COLLECTION_DELETE_DONE'
  data: { collectionId: number }
}

type FetchWebsocketLinkDoneAction = APIDoneAction & {
  type: 'FETCH_WEBSOCKET_LINK_DONE'
  data: {
    profileId: number
    response: {
      subscription: Subscription
      owned_tracks: Array<number>
    }
  }
}

type FetchEntitiesDoneAction = APIDoneAction & {
  type: 'OTHER' // NOTE(Kitti): Just need something
  merge?: boolean
  data?: {
    response?: {
      entities?: EntitiesState
    }
  }
}

type EntitiesReducerActionTypes =
  | DeleteUserSongDoneAction
  | CollectionDeleteDoneAction
  | FetchWebsocketLinkDoneAction
  | FetchEntitiesDoneAction

const reducer = (state: EntitiesState = initialEntities, action: EntitiesReducerActionTypes): EntitiesState => {
  switch (action.type) {
    case 'DELETE_USER_SONG_DONE': {
      const { userSongs } = state
      delete userSongs[action.data.userSongId]
      return {
        ...state,
        userSongs,
      }
    }

    case 'COLLECTION_DELETE_DONE': {
      const { collections } = state
      delete collections[action.data.collectionId]
      return {
        ...state,
        collections,
      }
    }

    case 'FETCH_WEBSOCKET_LINK_DONE': {
      const {
        data: {
          profileId,
          response: { subscription, owned_tracks },
        },
      } = action

      const purchasedTracks = state.profiles[profileId].purchased_tracks
      return {
        ...state,
        subscriptions: {
          ...state.subscriptions,
          [subscription.id]: subscription,
        },
        profiles: {
          ...state.profiles,
          [profileId]: {
            ...state.profiles[profileId],
            purchased_tracks: [...purchasedTracks, ...owned_tracks],
          },
        },
      }
    }

    default: {
      if (action?.data?.response?.entities) {
        const { entities } = action?.data?.response
        if (action.merge) {
          return merge({}, entities, state)
        }
        return Object.keys(entities || {}).reduce(
          (agg, entityType) => ({
            ...agg,
            [entityType]: {
              ...state[entityType as keyof EntitiesState],
              ...entities[entityType as keyof EntitiesState],
            },
          }),
          state,
        )
      }
      return state
    }
  }
}

export default reducer
